import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaArrowRight, FaSpinner, FaMapMarkerAlt, FaImages, FaStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig.js';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');

      const postsResponse = await api.get('/posts', { 
        params: { 
          status: 1
        } 
      });

      if (postsResponse.data.success) {
        const posts = postsResponse.data.data || [];
        
        const eventsWithPictures = await Promise.all(
          posts.map(async (post) => {
            try {
              const picturesResponse = await api.get('/pictures', {
                params: {
                  post_id: post.id
                }
              });
              
              let images = [];
              if (picturesResponse.data.success) {
                images = picturesResponse.data.data.filter(image => 
                  image.post_id === post.id
                );
              }
              
              return {
                ...post,
                images: images,
                hasImages: images.length > 0
              };
            } catch (pictureError) {
              return {
                ...post,
                images: [],
                hasImages: false
              };
            }
          })
        );

        setEvents(eventsWithPictures);
      } else {
        setError(postsResponse.data.message || 'Failed to load events');
      }
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message.includes('Network Error')) {
        setError('Cannot connect to the server. Please try again later.');
      } else {
        setError('Unable to load events. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBA';
    
    try {
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Date TBA';
    }
  };

  const getEventImage = (event) => {
    if (event.images && event.images.length > 0) {
      const image = event.images[0];
      let imageUrl = image.picture_path || image.url || image.path;
      
      if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
        imageUrl = `/${imageUrl}`;
      }
      
      if (imageUrl && imageUrl.startsWith('/') && !imageUrl.startsWith('http')) {
        imageUrl = `http://192.168.100.101:8000${imageUrl}`;
      }
      
      return imageUrl;
    }
    return '/images/event-placeholder.jpg';
  };

  const truncateText = (htmlText, maxLength) => {
    if (!htmlText) return 'No description available';
    const plainText = htmlText.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    if (plainText.length <= maxLength) return plainText;
    return plainText.substr(0, maxLength) + '...';
  };

  // Show only upcoming events (or all if no upcoming)
  const getDisplayEvents = () => {
    const now = new Date();
    const upcomingEvents = events.filter(event => {
      if (!event.event_date) return true;
      try {
        const eventDate = new Date(event.event_date);
        return !isNaN(eventDate.getTime()) && eventDate >= now;
      } catch {
        return true;
      }
    });
    
    // If there are upcoming events, show them. Otherwise show all events.
    return upcomingEvents.length > 0 ? upcomingEvents.slice(0, 6) : events.slice(0, 6);
  };

  const displayEvents = getDisplayEvents();

  const handleEventClick = (eventId) => {
    navigate(`/event/${eventId}`);
  };

  if (loading) {
    return (
      <section className="events-listing">
        <div className="container">
          <div className="section-title">
            <h2>Our Events</h2>
            <p>Join us for events that empower, educate, and inspire</p>
          </div>
          <div className="loading-container">
            <FaSpinner className="spinner" />
            <p>Loading events from Nengai Foundation...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="events-listing">
        <div className="container">
          <div className="section-title">
            <h2>Our Events</h2>
            <p className="error-text">{error}</p>
          </div>
          <div className="events-error">
            <button onClick={fetchEvents} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="events-listing">
      <div className="container">
        <div className="section-title">
          <h2>Our Events</h2>
          <p>Join us for events that empower, educate, and inspire children with autism and their families</p>
        </div>

        {displayEvents.length === 0 ? (
          <div className="no-events">
            <div className="no-events-icon">
              <FaImages />
            </div>
            <h3>No events scheduled</h3>
            <p>There are no events to display at the moment. Check back soon for new events!</p>
            <button onClick={fetchEvents} className="retry-btn">
              Refresh Events
            </button>
          </div>
        ) : (
          <>
            <div className="events-grid">
              {displayEvents.map(event => (
                <div className="event-card" key={event.id}>
                  <div className="event-image">
                    <img 
                      src={getEventImage(event)} 
                      alt={event.title}
                      onError={(e) => {
                        e.target.src = '/images/event-placeholder.jpg';
                      }}
                      loading="lazy"
                    />
                    {event.featured && (
                      <span className="featured-badge">
                        <FaStar className="featured-icon" />
                        Featured
                      </span>
                    )}
                    {!event.hasImages && (
                      <div className="no-image-placeholder">
                        <FaImages className="placeholder-icon" />
                        <span>No Images</span>
                      </div>
                    )}
                    <div className="event-category-badge">
                      {event.category || 'Event'}
                    </div>
                  </div>
                  
                  <div className="event-content">
                    <div className="event-meta">
                      <span className="event-date">
                        <FaCalendarAlt className="meta-icon" />
                        {formatDate(event.event_date)}
                      </span>
                    </div>
                    
                    <h3>{event.title}</h3>
                    
                    <div className="event-description">
                      {truncateText(event.post, 120)}
                    </div>

                    <div className="event-footer">
                      {event.location && (
                        <div className="event-location">
                          <FaMapMarkerAlt className="location-icon" />
                          {event.location}
                        </div>
                      )}
                      <button
                        className="read-more"
                        onClick={() => handleEventClick(event.id)}
                      >
                        Learn More <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* View All Events Button - Only show if there are more events */}
            {events.length > 6 && (
              <div className="view-all-events">
                <button 
                  className="view-all-btn"
                  onClick={() => navigate('/events')}
                >
                  View All Events ({events.length})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Events;